(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setupGallery = (root) => {
    const gallery = root.querySelector('[data-deposit-gallery]');
    const mainImage = gallery?.querySelector('[data-deposit-main-image]');
    const figure = mainImage?.closest('figure');
    const buttons = [...(gallery?.querySelectorAll('[data-deposit-thumb]') || [])];
    if (!mainImage || !figure || !buttons.length) return;

    const activate = (button) => {
      const source = button.dataset.imageSrc;
      if (!source || source === mainImage.getAttribute('src')) return;

      buttons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });

      figure.classList.add('is-changing');
      const preload = new Image();
      preload.onload = () => {
        mainImage.src = source;
        mainImage.alt = button.dataset.imageAlt || '';
        window.requestAnimationFrame(() => figure.classList.remove('is-changing'));
      };
      preload.onerror = () => figure.classList.remove('is-changing');
      preload.src = source;
    };

    buttons.forEach((button) => button.addEventListener('click', () => activate(button)));
  };

  const setupAccordions = (root) => {
    const groups = [
      [...root.querySelectorAll('.id-reserve__accordions details')],
      [...root.querySelectorAll('.id-reserve__faq-list details')],
    ];

    groups.forEach((details) => {
      details.forEach((item) => {
        item.addEventListener('toggle', () => {
          if (!item.open) return;
          details.forEach((other) => {
            if (other !== item) other.open = false;
          });
        });
      });
    });
  };

  const setupBenefitSwitcher = (root) => {
    const switcher = root.querySelector('[data-benefit-switcher]');
    if (!switcher) return;

    const details = [...switcher.querySelectorAll('[data-benefit-option]')];
    const images = [...switcher.querySelectorAll('[data-benefit-image]')];

    const activate = (item) => {
      const key = item.dataset.benefitOption;
      images.forEach((image) => {
        const active = image.dataset.benefitImage === key;
        image.classList.toggle('is-active', active);
        image.setAttribute('aria-hidden', String(!active));
      });
    };

    details.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (item.open) activate(item);
      });
    });

    activate(details.find((item) => item.open) || details[0]);
  };

  const setupMobileBenefitCarousel = (root) => {
    const carousel = root.querySelector('[data-mobile-benefit-carousel]');
    const rail = carousel?.querySelector('[data-mobile-benefit-rail]');
    const slides = [...(rail?.querySelectorAll('.id-reserve__benefit-card') || [])];
    const current = carousel?.querySelector('[data-mobile-benefit-current]');
    const previous = carousel?.querySelector('[data-mobile-benefit-prev]');
    const next = carousel?.querySelector('[data-mobile-benefit-next]');
    if (!carousel || !rail || !slides.length) return;

    let activeIndex = 0;
    let frameRequested = false;

    const update = () => {
      frameRequested = false;
      activeIndex = slides.reduce((closest, slide, index) => {
        const distance = Math.abs(slide.offsetLeft - rail.scrollLeft);
        return distance < closest.distance ? { index, distance } : closest;
      }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;

      if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      if (previous) previous.disabled = activeIndex === 0;
      if (next) next.disabled = activeIndex === slides.length - 1;
    };

    const requestUpdate = () => {
      if (frameRequested) return;
      frameRequested = true;
      window.requestAnimationFrame(update);
    };

    const goTo = (index) => {
      const targetIndex = Math.max(0, Math.min(slides.length - 1, index));
      rail.scrollTo({ left: slides[targetIndex].offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
    };

    previous?.addEventListener('click', () => goTo(activeIndex - 1));
    next?.addEventListener('click', () => goTo(activeIndex + 1));
    rail.addEventListener('scroll', requestUpdate, { passive: true });
    rail.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      goTo(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });
    window.addEventListener('resize', requestUpdate, { passive: true });
    update();
  };

  const setupThankYouLayer = (root) => {
    const layer = root.querySelector('[data-deposit-thanks]');
    if (!layer) return;

    const closeButton = layer.querySelector('[data-deposit-thanks-close]');
    const heading = layer.querySelector('[tabindex="-1"]');
    const returnFocus = root.querySelector('.id-reserve__campaign-button');
    const query = new URLSearchParams(window.location.search);
    const shouldOpen = query.get('reservation') === 'success' || window.location.hash === '#reservation-thank-you';

    const open = () => {
      layer.hidden = false;
      document.body.classList.add('id-reserve-thanks-open');
      window.requestAnimationFrame(() => heading?.focus());
    };

    const close = () => {
      layer.hidden = true;
      document.body.classList.remove('id-reserve-thanks-open');

      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('reservation');
      if (cleanUrl.hash === '#reservation-thank-you') cleanUrl.hash = '';
      window.history.replaceState({}, '', `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`);
      returnFocus?.focus();
    };

    closeButton?.addEventListener('click', close);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !layer.hidden) close();
    });
    window.addEventListener('inkanva:reservation-complete', open);

    if (shouldOpen) open();
  };

  const init = (root) => {
    if (root.dataset.depositReady === 'true') return;
    root.dataset.depositReady = 'true';
    setupGallery(root);
    setupAccordions(root);
    setupBenefitSwitcher(root);
    setupMobileBenefitCarousel(root);
    setupThankYouLayer(root);
  };

  const initAll = () => document.querySelectorAll('[data-deposit-root]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
